import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { ConfirmDialog } from './ConfirmDialog';
import { Edit2, Save, X, User, Baby, AlertTriangle, AlertCircle, CheckCircle, Mail, Phone, Trash2 } from 'lucide-react';
import { Adult, Child, Relationship, UpdateAdultRequest, UpdateChildRequest } from '../types/family.types';
import { Gender, RelationshipType, UserStatus } from '../types/enums';
import { useFamilyMutations } from '../hooks/useFamilyMutations';
import { getGenderOptions, getMonthOptions } from '../utils/form-options.utils';

interface EditableAdultCardProps {
  adult: Adult;
  familyId: string;
  isPrimary?: boolean;
  totalAdults?: number;
  children?: Child[];
  relationships?: Relationship[];
  familyStatus?: UserStatus;
}

export const EditableAdultCard = ({
  adult,
  familyId,
  isPrimary,
  totalAdults = 1,
  children = [],
  relationships = [],
  familyStatus,
}: EditableAdultCardProps) => {
  const { t } = useTranslation(['registration', 'common']);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formErrors, setFormErrors] = useState<Map<string, string>>(new Map());
  const [formData, setFormData] = useState({
    firstName: adult.firstName,
    lastName: adult.lastName,
    phone: adult.phone || '',
    gender: adult.gender || Gender.PREFER_NOT_TO_SAY,
    email: adult.email || '',
    birthYear: adult.birthYear,
    birthMonth: adult.birthMonth,
    consentForPhoto: adult.consentForPhoto || false,
  });

  const { updateAdult, updateFamily, deleteAdult, updateRelationship } = useFamilyMutations(familyId);

  const getErrorClass = (id: string) => (formErrors.has(id) ? 'border-red-500 bg-red-50/10' : '');

  const getErrorMessage = (id: string) => formErrors.get(id) || '';

  const scrollToError = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const errorBanner = document.getElementById(`error-banner-adult-${adult.id}`);
      if (errorBanner) {
        errorBanner.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        element.focus({ preventScroll: true });
      }, 300);
    }
  };

  // Get relationships for this adult
  const adultRelationships = relationships.filter((r) => r.adultId === adult.id);

  const relationshipOptions = [
    { value: RelationshipType.PARENT, label: t('relationshipOptions.parent') },
    { value: RelationshipType.GRANDPARENT, label: t('relationshipOptions.grandparent') },
    { value: RelationshipType.RELATIVE, label: t('relationshipOptions.relative') },
    { value: RelationshipType.AUTHORIZED, label: t('relationshipOptions.authorizedGuardian') },
    { value: RelationshipType.EMERGENCY, label: t('relationshipOptions.emergency') },
  ];

  const handleRelationshipChange = async (relationshipId: string, newType: RelationshipType) => {
    await updateRelationship.mutateAsync({ relationshipId, relationshipType: newType });
  };

  const canDelete = !isPrimary && totalAdults > 1;

  const handleDelete = async () => {
    if (familyStatus === UserStatus.PENDING) {
      const apiErrors = new Map<string, string>();
      apiErrors.set(`firstName-${adult.id}`, t('common:families.pendingDeleteError'));
      setFormErrors(apiErrors);
      scrollToError(`firstName-${adult.id}`);
      setShowDeleteDialog(false);
      return;
    }
    await deleteAdult.mutateAsync(adult.id);
    setFormErrors(new Map());
    setShowDeleteDialog(false);
  };

  // Sync form data when adult prop changes (after successful save)
  useEffect(() => {
    setFormData({
      firstName: adult.firstName,
      lastName: adult.lastName,
      phone: adult.phone || '',
      gender: adult.gender || Gender.PREFER_NOT_TO_SAY,
      email: adult.email || '',
      birthYear: adult.birthYear,
      birthMonth: adult.birthMonth,
      consentForPhoto: adult.consentForPhoto || false,
    });
  }, [adult]);

  const handleSave = async () => {
    if (familyStatus === UserStatus.PENDING) {
      const apiErrors = new Map<string, string>();
      apiErrors.set(`firstName-${adult.id}`, t('common:families.pendingEditError'));
      setFormErrors(apiErrors);
      scrollToError(`firstName-${adult.id}`);
      return;
    }
    const updateData: UpdateAdultRequest = {};
    const lastNameChanged = formData.lastName !== adult.lastName;

    if (formData.firstName !== adult.firstName) {
      updateData.firstName = formData.firstName;
    }
    if (lastNameChanged) {
      updateData.lastName = formData.lastName;
    }
    if (formData.phone !== (adult.phone || '')) {
      updateData.phone = formData.phone || undefined;
    }
    if (formData.gender !== adult.gender) {
      updateData.gender = formData.gender;
    }
    // Email can only be updated for non-primary adults
    if (!isPrimary && formData.email !== (adult.email || '')) {
      updateData.email = formData.email || undefined;
    }
    // Birth date - send both if either changed
    if (formData.birthYear !== adult.birthYear || formData.birthMonth !== adult.birthMonth) {
      updateData.birthYear = formData.birthYear;
      updateData.birthMonth = formData.birthMonth;
    }

    // Photo consent
    if (formData.consentForPhoto !== (adult.consentForPhoto || false)) {
      updateData.consentForPhoto = formData.consentForPhoto;
    }

    if (Object.keys(updateData).length > 0) {
      try {
        await updateAdult.mutateAsync({ adultId: adult.id, data: updateData });
      } catch (error) {
        const apiErrors = new Map<string, string>();
        const errorMsg =
          error instanceof Error ? error.message : String((error as Record<string, unknown>)?.message ?? '');

        const phoneErrorCodes: Record<string, string> = {
          INVALID_AREA_CODE_PREFIX: 'validation.phoneAreaCodePrefix',
          INVALID_CANADIAN_AREA_CODE: 'validation.phoneCanadianAreaCode',
          INVALID_EXCHANGE_CODE: 'validation.phoneExchangeCodePrefix',
          INVALID_PATTERN: 'validation.phonePattern',
          INVALID_E164: 'validation.phoneE164',
        };

        if (errorMsg.includes('First name') || errorMsg.includes('firstName')) {
          apiErrors.set(`firstName-${adult.id}`, errorMsg);
        } else if (errorMsg.includes('Last name') || errorMsg.includes('lastName')) {
          apiErrors.set(`lastName-${adult.id}`, errorMsg);
        } else if (errorMsg.includes('Email') || errorMsg.includes('email')) {
          apiErrors.set(`email-${adult.id}`, errorMsg);
        } else if (errorMsg.includes('Phone') || errorMsg.includes('phone')) {
          let phoneMsg = errorMsg;
          for (const [code, i18nKey] of Object.entries(phoneErrorCodes)) {
            if (errorMsg.includes(code)) {
              phoneMsg = t(i18nKey);
              break;
            }
          }
          apiErrors.set(`phone-${adult.id}`, phoneMsg);
        } else if (errorMsg.includes('Birth year') || errorMsg.includes('birthYear')) {
          apiErrors.set(`birthYear-${adult.id}`, errorMsg);
        } else if (errorMsg.includes('Birth month') || errorMsg.includes('birthMonth')) {
          apiErrors.set(`birthMonth-${adult.id}`, errorMsg);
        } else if (errorMsg.includes('age') || errorMsg.includes('14 years')) {
          apiErrors.set(`birthYear-${adult.id}`, t('validation.adultTooYoung'));
        } else if (errorMsg.includes('already exists')) {
          apiErrors.set(`email-${adult.id}`, t('validation.emailAlreadyExists'));
        } else if (errorMsg) {
          apiErrors.set(`firstName-${adult.id}`, errorMsg);
        } else {
          apiErrors.set(`firstName-${adult.id}`, t('common:families.updateError'));
        }

        setFormErrors(apiErrors);
        if (apiErrors.size > 0) {
          const firstErrorId = apiErrors.keys().next().value as string;
          scrollToError(firstErrorId);
        }
        return;
      }
    }

    // Update family name if primary adult's last name changed
    if (isPrimary && lastNameChanged) {
      try {
        await updateFamily.mutateAsync({ name: `${formData.lastName} Family` });
      } catch (error) {
        const apiErrors = new Map<string, string>();
        const errorMsg =
          error instanceof Error ? error.message : String((error as Record<string, unknown>)?.message ?? '');
        apiErrors.set(`lastName-${adult.id}`, errorMsg || t('common:families.updateError'));
        setFormErrors(apiErrors);
        scrollToError(`lastName-${adult.id}`);
        return;
      }
    }

    setFormErrors(new Map());
    setIsEditing(false);
  };

  const hasChanges = () => {
    return (
      formData.firstName !== adult.firstName ||
      formData.lastName !== adult.lastName ||
      formData.phone !== (adult.phone || '') ||
      formData.gender !== (adult.gender || Gender.PREFER_NOT_TO_SAY) ||
      formData.email !== (adult.email || '') ||
      formData.birthYear !== adult.birthYear ||
      formData.birthMonth !== adult.birthMonth ||
      formData.consentForPhoto !== (adult.consentForPhoto || false)
    );
  };

  const handleCancelClick = () => {
    if (hasChanges()) {
      setShowConfirmDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleConfirmDiscard = () => {
    setFormData({
      firstName: adult.firstName,
      lastName: adult.lastName,
      phone: adult.phone || '',
      gender: adult.gender || Gender.PREFER_NOT_TO_SAY,
      email: adult.email || '',
      birthYear: adult.birthYear,
      birthMonth: adult.birthMonth,
      consentForPhoto: adult.consentForPhoto || false,
    });
    setShowConfirmDialog(false);
    setIsEditing(false);
  };

  const genderOptions = getGenderOptions(t);
  const monthOptions = getMonthOptions(t);

  if (!isEditing) {
    return (
      <Card className="relative">
        <CardContent className="p-4">
          {isPrimary && (
            <div className="absolute -top-2 right-2">
              <Badge variant="default" className="text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                {t('labels.primary')}
              </Badge>
            </div>
          )}
          <ConfirmDialog
            isOpen={showDeleteDialog}
            title={t('profile.removeAdult')}
            message={t('profile.removeConfirm', { name: `${adult.firstName} ${adult.lastName}` })}
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteDialog(false)}
            confirmLabel={deleteAdult.isPending ? t('profile.removing') : t('profile.remove')}
            variant="danger"
          />
          <div className={`flex items-start justify-between gap-2 ${isPrimary ? 'pt-4' : ''}`}>
            <div className="flex items-start space-x-3 min-w-0">
              <div className="rounded-full bg-[var(--color-secondary-100)] p-2 shrink-0">
                <User className="h-5 w-5 text-[var(--color-secondary-700)]" />
              </div>
              <div className="min-w-0">
                <h4 className="font-medium text-sm sm:text-base text-[var(--color-neutral-900)] truncate">
                  {adult.firstName} {adult.lastName}
                </h4>
                {adult.email && (
                  <p className="text-xs sm:text-sm text-[var(--color-neutral-600)] flex items-center gap-1 mt-1 truncate">
                    <Mail className="h-3 w-3 shrink-0" />
                    <span className="truncate">{adult.email}</span>
                  </p>
                )}
                {adult.phone && (
                  <p className="text-xs sm:text-sm text-[var(--color-neutral-600)] flex items-center gap-1">
                    <Phone className="h-3 w-3 shrink-0" />
                    {adult.phone}
                  </p>
                )}
                {adult.consentForPhoto !== undefined && (
                  <div className="mt-1.5">
                    <Badge variant={adult.consentForPhoto ? 'default' : 'secondary'} className="text-xs">
                      {t('profile.photoConsent')}:{' '}
                      {adult.consentForPhoto ? t('binaryOptions.yes') : t('binaryOptions.no')}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                <Edit2 className="h-4 w-4" />
              </Button>
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-[var(--color-error-600)] hover:text-[var(--color-error-700)] hover:bg-[var(--color-error-100)]"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="ring-2 ring-[var(--color-primary-500)]">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-[var(--color-secondary-700)]" />
            <span className="font-medium text-sm sm:text-base">{t('profile.editAdult')}</span>
          </div>
          <div className="flex gap-2 self-end sm:self-auto">
            <Button variant="outline" size="sm" onClick={handleCancelClick} disabled={updateAdult.isPending}>
              <X className="h-4 w-4 mr-1" />
              {t('profile.cancel')}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={updateAdult.isPending}>
              <Save className="h-4 w-4 mr-1" />
              {updateAdult.isPending ? t('profile.saving') : t('profile.save')}
            </Button>
          </div>
        </div>

        {formErrors.size > 0 && (
          <div
            id={`error-banner-adult-${adult.id}`}
            className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 rounded-r-lg mb-4 shadow-md sticky top-0 z-10"
            role="alert"
            aria-live="polite"
          >
            <div className="flex gap-2 sm:gap-3">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-red-800 mb-2">
                  {t('validation.adultValidationFailed')}
                </h3>
                <div className="text-xs sm:text-sm text-red-700 space-y-1">
                  {Array.from(formErrors.values())
                    .slice(0, 5)
                    .map((error, idx) => (
                      <div key={idx} className="flex items-start gap-1.5 break-words">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>{error}</span>
                      </div>
                    ))}
                  {formErrors.size > 5 && (
                    <div className="text-xs sm:text-sm text-red-700 mt-2">
                      {t('validation.andMore', { count: formErrors.size - 5 })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog
          isOpen={showConfirmDialog}
          title={t('profile.discardChanges')}
          message={t('profile.discardConfirm')}
          onConfirm={handleConfirmDiscard}
          onCancel={() => setShowConfirmDialog(false)}
        />

        <div className="space-y-3">
          {/* Name Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div>
              <Label
                htmlFor={`firstName-${adult.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.firstName')}
              </Label>
              <Input
                id={`firstName-${adult.id}`}
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className={`mt-1 text-base ${getErrorClass(`firstName-${adult.id}`)}`}
              />
              {getErrorMessage(`firstName-${adult.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`firstName-${adult.id}`)}</p>
                </div>
              )}
            </div>
            <div>
              <Label
                htmlFor={`lastName-${adult.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.lastName')}
              </Label>
              <Input
                id={`lastName-${adult.id}`}
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className={`mt-1 text-base ${getErrorClass(`lastName-${adult.id}`)}`}
              />
              {getErrorMessage(`lastName-${adult.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`lastName-${adult.id}`)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Email & Phone Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div>
              <Label
                htmlFor={`email-${adult.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.email')} {isPrimary && `(${t('profile.readOnly')})`}
              </Label>
              <Input
                id={`email-${adult.id}`}
                type="email"
                value={isPrimary ? adult.email || '' : formData.email}
                onChange={(e) => !isPrimary && setFormData({ ...formData, email: e.target.value })}
                disabled={isPrimary}
                className={`mt-1 text-base ${isPrimary ? 'bg-[var(--color-neutral-100)] cursor-not-allowed' : ''} ${getErrorClass(`email-${adult.id}`)}`}
              />
              {getErrorMessage(`email-${adult.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`email-${adult.id}`)}</p>
                </div>
              )}
              {!getErrorMessage(`email-${adult.id}`) && isPrimary && (
                <p className="text-xs text-[var(--color-neutral-500)] mt-0.5">{t('profile.cannotBeChanged')}</p>
              )}
            </div>
            <div>
              <Label
                htmlFor={`phone-${adult.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.phone')}
              </Label>
              <Input
                id={`phone-${adult.id}`}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder={t('placeholders.phone')}
                type="tel"
                className={`mt-1 text-base ${getErrorClass(`phone-${adult.id}`)}`}
              />
              {getErrorMessage(`phone-${adult.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`phone-${adult.id}`)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Gender & DOB Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
            <div className="sm:col-span-2 md:col-span-1">
              <Label
                htmlFor={`gender-${adult.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.gender')}
              </Label>
              <select
                id={`gender-${adult.id}`}
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value as Gender })}
                className="mt-1 w-full px-3 py-2.5 sm:py-2 border rounded-md bg-white text-base"
              >
                {genderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label
                htmlFor={`birthMonth-${adult.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.birthMonth')}
              </Label>
              <select
                id={`birthMonth-${adult.id}`}
                value={formData.birthMonth || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    birthMonth: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                className={`mt-1 w-full px-3 py-2.5 sm:py-2 border rounded-md bg-white text-base ${getErrorClass(`birthMonth-${adult.id}`)}`}
              >
                <option value="">{t('placeholders.selectMonth')}</option>
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              {getErrorMessage(`birthMonth-${adult.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`birthMonth-${adult.id}`)}</p>
                </div>
              )}
            </div>
            <div>
              <Label
                htmlFor={`birthYear-${adult.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.birthYear')}
              </Label>
              <select
                id={`birthYear-${adult.id}`}
                value={formData.birthYear || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    birthYear: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                className={`mt-1 w-full px-3 py-2.5 sm:py-2 border rounded-md bg-white text-base ${getErrorClass(`birthYear-${adult.id}`)}`}
              >
                <option value="">{t('placeholders.selectYear')}</option>
                {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {getErrorMessage(`birthYear-${adult.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`birthYear-${adult.id}`)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Photo Release Consent */}
          <div className="space-y-1.5 pt-2 border-t border-[var(--color-neutral-200)]">
            <Label className="text-sm font-medium">{t('photoRelease.title')}</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, consentForPhoto: true })}
                className={`p-2.5 sm:p-3 rounded-lg border-2 text-sm font-medium transition-all active:scale-95 ${
                  formData.consentForPhoto
                    ? 'border-[var(--color-success-500)] bg-[var(--color-success-100)] text-[var(--color-success-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <CheckCircle
                  className={`h-4 w-4 mx-auto mb-1 ${formData.consentForPhoto ? 'text-[var(--color-success-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.yes')}
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, consentForPhoto: false })}
                className={`p-2.5 sm:p-3 rounded-lg border-2 text-sm font-medium transition-all active:scale-95 ${
                  !formData.consentForPhoto
                    ? 'border-[var(--color-error-500)] bg-[var(--color-error-100)] text-[var(--color-error-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <X
                  className={`h-4 w-4 mx-auto mb-1 ${!formData.consentForPhoto ? 'text-[var(--color-error-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.no')}
              </button>
            </div>
          </div>

          {/* Child Relationships Section */}
          {children.length > 0 && adultRelationships.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-[var(--color-neutral-200)]">
              <Label className="text-sm font-medium">{t('profile.relationshipToChildren')}</Label>
              <div className="space-y-2">
                {adultRelationships.map((rel) => {
                  const child = children.find((c) => c.id === rel.childId);
                  if (!child) return null;
                  return (
                    <div
                      key={rel.id}
                      className="flex items-center justify-between gap-2 p-2 bg-[var(--color-neutral-50)] rounded-lg"
                    >
                      <span className="text-sm text-[var(--color-neutral-700)] truncate">
                        {child.firstName} {child.lastName}
                      </span>
                      <select
                        value={rel.relationshipType}
                        onChange={(e) => handleRelationshipChange(rel.id, e.target.value as RelationshipType)}
                        disabled={updateRelationship.isPending}
                        className="text-sm px-2 py-1 border rounded-md bg-white w-full max-w-[140px]"
                      >
                        {relationshipOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

interface EditableChildCardProps {
  child: Child;
  familyId: string;
  familyStatus?: UserStatus;
}

export const EditableChildCard = ({ child, familyId, familyStatus }: EditableChildCardProps) => {
  const { t } = useTranslation(['registration', 'common']);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formErrors, setFormErrors] = useState<Map<string, string>>(new Map());
  const [formData, setFormData] = useState({
    firstName: child.firstName,
    lastName: child.lastName,
    hasSpecialNeeds: child.hasSpecialNeeds,
    consentForPhoto: child.consentForPhoto || false,
    gender: child.gender || Gender.PREFER_NOT_TO_SAY,
    birthYear: child.birthYear,
    birthMonth: child.birthMonth,
  });

  const { updateChild, deleteChild } = useFamilyMutations(familyId);

  const getErrorClass = (id: string) => (formErrors.has(id) ? 'border-red-500 bg-red-50/10' : '');

  const getErrorMessage = (id: string) => formErrors.get(id) || '';

  const scrollToError = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const errorBanner = document.getElementById(`error-banner-child-${child.id}`);
      if (errorBanner) {
        errorBanner.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        element.focus({ preventScroll: true });
      }, 300);
    }
  };

  const handleDelete = async () => {
    if (familyStatus === UserStatus.PENDING) {
      const apiErrors = new Map<string, string>();
      apiErrors.set(`firstName-${child.id}`, t('common:families.pendingDeleteError'));
      setFormErrors(apiErrors);
      scrollToError(`firstName-${child.id}`);
      setShowDeleteDialog(false);
      return;
    }
    await deleteChild.mutateAsync(child.id);
    setFormErrors(new Map());
    setShowDeleteDialog(false);
  };

  // Sync form data when child prop changes (after successful save)
  useEffect(() => {
    setFormData({
      firstName: child.firstName,
      lastName: child.lastName,
      hasSpecialNeeds: child.hasSpecialNeeds,
      consentForPhoto: child.consentForPhoto || false,
      gender: child.gender || Gender.PREFER_NOT_TO_SAY,
      birthYear: child.birthYear,
      birthMonth: child.birthMonth,
    });
  }, [child]);

  const showConsentWarning = child.consentForPhoto === true && formData.consentForPhoto === false;

  const handleSave = async () => {
    if (familyStatus === UserStatus.PENDING) {
      const apiErrors = new Map<string, string>();
      apiErrors.set(`firstName-${child.id}`, t('common:families.pendingEditError'));
      setFormErrors(apiErrors);
      scrollToError(`firstName-${child.id}`);
      return;
    }
    const updateData: UpdateChildRequest = {};

    if (formData.firstName !== child.firstName) {
      updateData.firstName = formData.firstName;
    }
    if (formData.lastName !== child.lastName) {
      updateData.lastName = formData.lastName;
    }
    if (formData.hasSpecialNeeds !== child.hasSpecialNeeds) {
      updateData.hasSpecialNeeds = formData.hasSpecialNeeds;
    }
    if (formData.consentForPhoto !== child.consentForPhoto) {
      updateData.consentForPhoto = formData.consentForPhoto;
    }
    if (formData.gender !== child.gender) {
      updateData.gender = formData.gender;
    }
    // Birth date - send both if either changed
    if (formData.birthYear !== child.birthYear || formData.birthMonth !== child.birthMonth) {
      updateData.birthYear = formData.birthYear;
      updateData.birthMonth = formData.birthMonth;
    }

    if (Object.keys(updateData).length > 0) {
      try {
        await updateChild.mutateAsync({ childId: child.id, data: updateData });
      } catch (error) {
        const apiErrors = new Map<string, string>();
        const errorMsg =
          error instanceof Error ? error.message : String((error as Record<string, unknown>)?.message ?? '');

        if (errorMsg.includes('First name') || errorMsg.includes('firstName')) {
          apiErrors.set(`firstName-${child.id}`, errorMsg);
        } else if (errorMsg.includes('Last name') || errorMsg.includes('lastName')) {
          apiErrors.set(`lastName-${child.id}`, errorMsg);
        } else if (errorMsg.includes('Birth year') || errorMsg.includes('birthYear')) {
          apiErrors.set(`birthYear-${child.id}`, errorMsg);
        } else if (errorMsg.includes('Birth month') || errorMsg.includes('birthMonth')) {
          apiErrors.set(`birthMonth-${child.id}`, errorMsg);
        } else if (errorMsg.includes('age') || errorMsg.includes('18 years')) {
          apiErrors.set(`birthYear-${child.id}`, t('validation.childTooOld'));
        } else if (errorMsg) {
          apiErrors.set(`firstName-${child.id}`, errorMsg);
        } else {
          apiErrors.set(`firstName-${child.id}`, t('common:families.updateError'));
        }

        setFormErrors(apiErrors);
        if (apiErrors.size > 0) {
          const firstErrorId = apiErrors.keys().next().value as string;
          scrollToError(firstErrorId);
        }
        return;
      }
    }
    setFormErrors(new Map());
    setIsEditing(false);
  };

  const hasChanges = () => {
    return (
      formData.firstName !== child.firstName ||
      formData.lastName !== child.lastName ||
      formData.hasSpecialNeeds !== child.hasSpecialNeeds ||
      formData.consentForPhoto !== (child.consentForPhoto || false) ||
      formData.gender !== (child.gender || Gender.PREFER_NOT_TO_SAY) ||
      formData.birthYear !== child.birthYear ||
      formData.birthMonth !== child.birthMonth
    );
  };

  const handleCancelClick = () => {
    if (hasChanges()) {
      setShowConfirmDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleConfirmDiscard = () => {
    setFormData({
      firstName: child.firstName,
      lastName: child.lastName,
      hasSpecialNeeds: child.hasSpecialNeeds,
      consentForPhoto: child.consentForPhoto || false,
      gender: child.gender || Gender.PREFER_NOT_TO_SAY,
      birthYear: child.birthYear,
      birthMonth: child.birthMonth,
    });
    setShowConfirmDialog(false);
    setIsEditing(false);
  };

  const genderOptions = getGenderOptions(t);
  const monthOptions = getMonthOptions(t);

  if (!isEditing) {
    return (
      <Card>
        <CardContent className="p-4">
          <ConfirmDialog
            isOpen={showDeleteDialog}
            title={t('profile.removeChild')}
            message={t('profile.removeChildConfirm', { name: `${child.firstName} ${child.lastName}` })}
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteDialog(false)}
            confirmLabel={deleteChild.isPending ? t('profile.removing') : t('profile.remove')}
            variant="danger"
          />
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start space-x-3 min-w-0">
              <div className="rounded-full bg-[var(--color-primary-100)] p-2 shrink-0">
                <Baby className="h-5 w-5 text-[var(--color-primary-700)]" />
              </div>
              <div className="min-w-0">
                <h4 className="font-medium text-[var(--color-neutral-900)] truncate">
                  {child.firstName} {child.lastName}
                </h4>
                <p className="text-sm text-[var(--color-neutral-600)]">
                  {t('profile.age', { age: child.age || child.currentAge })}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {child.hasSpecialNeeds && (
                    <Badge variant="outline" className="text-xs">
                      {t('profile.specialNeeds')}
                    </Badge>
                  )}
                  <Badge variant={child.consentForPhoto ? 'default' : 'secondary'} className="text-xs">
                    {t('profile.photoConsent')}:{' '}
                    {child.consentForPhoto ? t('binaryOptions.yes') : t('binaryOptions.no')}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="text-[var(--color-error-600)] hover:text-[var(--color-error-700)] hover:bg-[var(--color-error-100)]"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="ring-2 ring-[var(--color-primary-500)]">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Baby className="h-5 w-5 text-[var(--color-primary-700)]" />
            <span className="font-medium text-sm sm:text-base">{t('profile.editChild')}</span>
          </div>
          <div className="flex gap-2 self-end sm:self-auto">
            <Button variant="outline" size="sm" onClick={handleCancelClick} disabled={updateChild.isPending}>
              <X className="h-4 w-4 mr-1" />
              {t('profile.cancel')}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={updateChild.isPending}>
              <Save className="h-4 w-4 mr-1" />
              {updateChild.isPending ? t('profile.saving') : t('profile.save')}
            </Button>
          </div>
        </div>

        {formErrors.size > 0 && (
          <div
            id={`error-banner-child-${child.id}`}
            className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 rounded-r-lg mb-4 shadow-md sticky top-0 z-10"
            role="alert"
            aria-live="polite"
          >
            <div className="flex gap-2 sm:gap-3">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-red-800 mb-2">
                  {t('validation.childValidationFailed')}
                </h3>
                <div className="text-xs sm:text-sm text-red-700 space-y-1">
                  {Array.from(formErrors.values())
                    .slice(0, 5)
                    .map((error, idx) => (
                      <div key={idx} className="flex items-start gap-1.5 break-words">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>{error}</span>
                      </div>
                    ))}
                  {formErrors.size > 5 && (
                    <div className="text-xs sm:text-sm text-red-700 mt-2">
                      {t('validation.andMore', { count: formErrors.size - 5 })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog
          isOpen={showConfirmDialog}
          title={t('profile.discardChanges')}
          message={t('profile.discardConfirm')}
          onConfirm={handleConfirmDiscard}
          onCancel={() => setShowConfirmDialog(false)}
        />

        <div className="space-y-3">
          {/* Name Row - always 2 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div>
              <Label
                htmlFor={`firstName-${child.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.firstName')}
              </Label>
              <Input
                id={`firstName-${child.id}`}
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className={`mt-1 text-base ${getErrorClass(`firstName-${child.id}`)}`}
              />
              {getErrorMessage(`firstName-${child.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`firstName-${child.id}`)}</p>
                </div>
              )}
            </div>
            <div>
              <Label
                htmlFor={`lastName-${child.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.lastName')}
              </Label>
              <Input
                id={`lastName-${child.id}`}
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className={`mt-1 text-base ${getErrorClass(`lastName-${child.id}`)}`}
              />
              {getErrorMessage(`lastName-${child.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`lastName-${child.id}`)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Gender & DOB Row - responsive 3 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
            <div className="sm:col-span-2 md:col-span-1">
              <Label
                htmlFor={`gender-${child.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.gender')}
              </Label>
              <select
                id={`gender-${child.id}`}
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value as Gender })}
                className="mt-1 w-full px-3 py-2.5 sm:py-2 border rounded-md bg-white text-base"
              >
                {genderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label
                htmlFor={`birthMonth-${child.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.birthMonth')}
              </Label>
              <select
                id={`birthMonth-${child.id}`}
                value={formData.birthMonth || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    birthMonth: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                className={`mt-1 w-full px-3 py-2.5 sm:py-2 border rounded-md bg-white text-base ${getErrorClass(`birthMonth-${child.id}`)}`}
              >
                <option value="">{t('placeholders.selectMonth')}</option>
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              {getErrorMessage(`birthMonth-${child.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`birthMonth-${child.id}`)}</p>
                </div>
              )}
            </div>
            <div>
              <Label
                htmlFor={`birthYear-${child.id}`}
                className="text-sm font-medium sm:min-h-[2.5rem] flex items-end pb-1"
              >
                {t('profile.birthYear')}
              </Label>
              <select
                id={`birthYear-${child.id}`}
                value={formData.birthYear || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    birthYear: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                className={`mt-1 w-full px-3 py-2.5 sm:py-2 border rounded-md bg-white text-base ${getErrorClass(`birthYear-${child.id}`)}`}
              >
                <option value="">{t('placeholders.selectYear')}</option>
                {Array.from({ length: 18 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {getErrorMessage(`birthYear-${child.id}`) && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage(`birthYear-${child.id}`)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Special Needs */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">{t('profile.specialNeeds')}</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, hasSpecialNeeds: true })}
                className={`p-2.5 sm:p-3 rounded-lg border-2 text-sm font-medium transition-all active:scale-95 ${
                  formData.hasSpecialNeeds
                    ? 'border-[var(--color-primary-500)] bg-[var(--color-primary-100)] text-[var(--color-primary-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <CheckCircle
                  className={`h-4 w-4 mx-auto mb-1 ${formData.hasSpecialNeeds ? 'text-[var(--color-primary-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.yes')}
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, hasSpecialNeeds: false })}
                className={`p-2.5 sm:p-3 rounded-lg border-2 text-sm font-medium transition-all active:scale-95 ${
                  !formData.hasSpecialNeeds
                    ? 'border-[var(--color-primary-500)] bg-[var(--color-primary-100)] text-[var(--color-primary-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <X
                  className={`h-4 w-4 mx-auto mb-1 ${!formData.hasSpecialNeeds ? 'text-[var(--color-primary-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.no')}
              </button>
            </div>
          </div>

          {/* Photo Release Consent */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">{t('photoRelease.title')}</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, consentForPhoto: true })}
                className={`p-2.5 sm:p-3 rounded-lg border-2 text-sm font-medium transition-all active:scale-95 ${
                  formData.consentForPhoto
                    ? 'border-[var(--color-success-500)] bg-[var(--color-success-100)] text-[var(--color-success-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <CheckCircle
                  className={`h-4 w-4 mx-auto mb-1 ${formData.consentForPhoto ? 'text-[var(--color-success-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.yes')}
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, consentForPhoto: false })}
                className={`p-2.5 sm:p-3 rounded-lg border-2 text-sm font-medium transition-all active:scale-95 ${
                  !formData.consentForPhoto
                    ? 'border-[var(--color-error-500)] bg-[var(--color-error-100)] text-[var(--color-error-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <X
                  className={`h-4 w-4 mx-auto mb-1 ${!formData.consentForPhoto ? 'text-[var(--color-error-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.no')}
              </button>
            </div>

            {showConsentWarning && (
              <Alert className="border-yellow-500 bg-yellow-50 mt-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-sm text-yellow-800">
                  {t('profile.photoConsentWarning')}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
